/**
 * Gestión de gestores de una cuenta asesoría.
 * Los gestores son usuarios admin que acceden al panel de la asesoría
 * para ver y controlar las empresas cliente vinculadas.
 * Campos: solo nombre, email y contraseña. Sin calendarios, grupos ni ausencias.
 */
import { useState, useEffect } from 'react'
import { FiPlus, FiUser, FiMail, FiLock, FiEdit2, FiTrash2, FiX, FiCheckCircle, FiShield } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { API_URL } from '../config/api'

const GestionGestoresAsesoria = () => {
  const { empresaId, buildApiUrl } = useEmpresaGestion()
  const [gestores, setGestores] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | 'crear' | 'editar' | 'confirmar-eliminar'
  const [gestorSeleccionado, setGestorSeleccionado] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const [form, setForm] = useState({ nombre: '', email: '', password: '', passwordConfirm: '' })
  const [errores, setErrores] = useState({})

  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` })

  const apiUrl = (path) => {
    if (empresaId) return `${API_URL}${path}?empresa_id=${empresaId}`
    return `${API_URL}${path}`
  }

  useEffect(() => { cargarGestores() }, [empresaId])

  const cargarGestores = async () => {
    setLoading(true)
    try {
      const r = await fetch(apiUrl('/empleados'), { headers: headers() })
      if (r.ok) {
        const data = await r.json()
        // Solo mostrar admins (gestores)
        setGestores((data.empleados || []).filter(e => e.is_admin === 1))
      }
    } catch {
      toast.error('Error al cargar gestores')
    } finally {
      setLoading(false)
    }
  }

  const abrirCrear = () => {
    setForm({ nombre: '', email: '', password: '', passwordConfirm: '' })
    setErrores({})
    setModal('crear')
  }

  const abrirEditar = (gestor) => {
    setGestorSeleccionado(gestor)
    setForm({ nombre: gestor.nombre, email: gestor.email, password: '', passwordConfirm: '' })
    setErrores({})
    setModal('editar')
  }

  const cerrarModal = () => {
    setModal(null)
    setGestorSeleccionado(null)
    setForm({ nombre: '', email: '', password: '', passwordConfirm: '' })
    setErrores({})
  }

  const validar = () => {
    const e = {}
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio'
    if (!form.email.trim()) e.email = 'El email es obligatorio'
    if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email no válido'
    if (modal === 'crear' && !form.password) e.password = 'La contraseña es obligatoria'
    if (form.password && form.password.length < 6) e.password = 'Mínimo 6 caracteres'
    if (form.password && form.password !== form.passwordConfirm) e.passwordConfirm = 'Las contraseñas no coinciden'
    setErrores(e)
    return Object.keys(e).length === 0
  }

  const guardar = async () => {
    if (!validar()) return
    setGuardando(true)
    try {
      if (modal === 'crear') {
        const r = await fetch(apiUrl('/empleados'), {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ nombre: form.nombre, email: form.email, password: form.password, is_admin: true, rol: 'admin' })
        })
        const data = await r.json()
        if (!r.ok) throw new Error(data.error || 'Error al crear gestor')
        toast.success(`Gestor ${form.nombre} creado`)
      } else {
        const body = { nombre: form.nombre, email: form.email }
        if (form.password) body.password = form.password
        const r = await fetch(apiUrl(`/empleados/${gestorSeleccionado.id}`), {
          method: 'PUT',
          headers: headers(),
          body: JSON.stringify(body)
        })
        const data = await r.json()
        if (!r.ok) throw new Error(data.error || 'Error al actualizar gestor')
        toast.success('Gestor actualizado')
      }
      cerrarModal()
      cargarGestores()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminarGestor = async () => {
    if (!gestorSeleccionado) return
    try {
      const r = await fetch(apiUrl(`/empleados/${gestorSeleccionado.id}`), {
        method: 'DELETE',
        headers: headers()
      })
      if (!r.ok) {
        const data = await r.json()
        throw new Error(data.error || 'Error al eliminar')
      }
      toast.success(`${gestorSeleccionado.nombre} eliminado`)
      cerrarModal()
      cargarGestores()
    } catch (e) {
      toast.error(e.message)
    }
  }

  const renderInput = (label, Icon, name, type = 'text', placeholder, hint) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type={type}
          value={form[name]}
          onChange={e => { setForm(f => ({ ...f, [name]: e.target.value })); setErrores(er => ({ ...er, [name]: '' })) }}
          placeholder={placeholder}
          className={`w-full pl-9 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all ${
            errores[name]
              ? 'border-red-300 focus:ring-red-300/30 bg-red-50'
              : 'border-gray-200 focus:ring-teal-500/30 focus:border-teal-400 bg-gray-50'
          }`}
        />
      </div>
      {errores[name] && <p className="mt-1 text-xs text-red-500">{errores[name]}</p>}
      {hint && !errores[name] && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 tracking-tight">Gestores</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Usuarios con acceso al panel de esta asesoría
          </p>
        </div>
        <button
          onClick={abrirCrear}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
        >
          <FiPlus className="h-4 w-4" />
          Nuevo gestor
        </button>
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 p-4 bg-teal-50 border border-teal-100 rounded-xl text-sm text-teal-800">
        <FiShield className="h-4 w-4 mt-0.5 flex-shrink-0 text-teal-500" />
        <p>Estos usuarios pueden acceder al panel de la asesoría y gestionar las empresas cliente vinculadas. No tienen acceso a fichajes propios.</p>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="card-glass divide-y divide-gray-50">
          {[1,2].map(i => (
            <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-gray-100 rounded-full w-32" />
                <div className="h-2.5 bg-gray-100 rounded-full w-48" />
              </div>
            </div>
          ))}
        </div>
      ) : gestores.length === 0 ? (
        <div className="card-glass flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4">
            <FiUser className="h-7 w-7 text-teal-400" />
          </div>
          <p className="font-semibold text-gray-900 mb-1">Sin gestores aún</p>
          <p className="text-sm text-gray-400 max-w-xs mb-5">Crea el primer usuario gestor para que pueda acceder a esta asesoría</p>
          <button onClick={abrirCrear} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl transition-colors">
            <FiPlus className="h-4 w-4" />
            Crear primer gestor
          </button>
        </div>
      ) : (
        <div className="card-glass overflow-hidden">
          <div className="divide-y divide-gray-50">
            {gestores.map(g => (
              <div key={g.id} className="group flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm">
                  {g.nombre?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">{g.nombre}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                      <FiShield className="h-3 w-3" /> Gestor
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 truncate mt-0.5 flex items-center gap-1">
                    <FiMail className="h-3 w-3 flex-shrink-0" />
                    {g.email}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => abrirEditar(g)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                    title="Editar"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { setGestorSeleccionado(g); setModal('confirmar-eliminar') }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Eliminar"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal crear/editar */}
      {(modal === 'crear' || modal === 'editar') && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
                  <FiUser className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-none">
                    {modal === 'crear' ? 'Nuevo gestor' : 'Editar gestor'}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">Acceso al panel de la asesoría</p>
                </div>
              </div>
              <button onClick={cerrarModal} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {/* Campos */}
            <div className="px-6 py-5 space-y-4">
              {renderInput('Nombre completo', FiUser, 'nombre', 'text', 'Ej. María García')}
              {renderInput('Email', FiMail, 'email', 'email', 'gestor@asesoria.com')}
              {renderInput(
                modal === 'editar' ? 'Nueva contraseña (opcional)' : 'Contraseña *',
                FiLock, 'password', 'password',
                modal === 'editar' ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres',
                modal === 'crear' ? 'Mínimo 6 caracteres' : undefined
              )}
              {(modal === 'crear' || form.password) && renderInput(
                'Confirmar contraseña' + (modal === 'crear' ? ' *' : ''),
                FiLock, 'passwordConfirm', 'password',
                'Repite la contraseña'
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={cerrarModal} disabled={guardando} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                Cancelar
              </button>
              <button
                onClick={guardar}
                disabled={guardando}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {guardando ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Guardando...</>
                ) : (
                  <><FiCheckCircle className="h-4 w-4" />{modal === 'crear' ? 'Crear gestor' : 'Guardar cambios'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminar */}
      {modal === 'confirmar-eliminar' && gestorSeleccionado && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <FiTrash2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">¿Eliminar gestor?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  <strong>{gestorSeleccionado.nombre}</strong> perderá el acceso al panel de esta asesoría inmediatamente.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={cerrarModal} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                Cancelar
              </button>
              <button onClick={eliminarGestor} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GestionGestoresAsesoria
